import { isReferralAuthCompleteFields } from "@/src/lib/prior-authorization/referral-auth-completeness";
import { selectReferralAuthTrackingPriorAuthorization } from "@/src/lib/prior-authorization/referral-auth-prior-authorization-selection";
import { convertDateStringToUSFormat } from "@/src/lib/utils/date-and-phone-utils";
import { formatCPTCodeForDisplay } from "@/src/models/cpt";
import type {
  CommonProcedureTerminologyItem,
  PriorAuthorization,
} from "@/src/models/documents/prior-authorization/prior-authorization";
import type { Referral } from "@/src/models/documents/referral";

export type ReferralAuthOnFileSnapshot = {
  hasAuthOnFile: boolean;
  authorizationNumber: string;
  startDate: string;
  endDate: string;
  cptCodes: string;
  visits: string;
};

function formatCptCodes(list: CommonProcedureTerminologyItem[] | null | undefined): string {
  if (!list?.length) return "";

  return list.map((item) => formatCPTCodeForDisplay(item.code, item.modifier)).join(", ");
}

function formatAuthDate(date: string | null | undefined): string {
  const trimmed = date?.trim();

  return trimmed ? convertDateStringToUSFormat(trimmed) : "";
}

export function buildReferralAuthOnFileMessage(snapshot: ReferralAuthOnFileSnapshot, leadIn: string): string {
  const parts = [leadIn];

  if (snapshot.authorizationNumber) parts.push(`Authorization Number: ${snapshot.authorizationNumber}.`);
  if (snapshot.startDate) parts.push(`Start date: ${snapshot.startDate}.`);
  if (snapshot.endDate) parts.push(`End date: ${snapshot.endDate}.`);
  if (snapshot.cptCodes) parts.push(`CPT codes: ${snapshot.cptCodes}.`);
  if (snapshot.visits) parts.push(`Visits: ${snapshot.visits}.`);

  return parts.join(" ");
}

export function resolveReferralAuthOnFile(
  referral: Referral,
  linkedPriorAuths: PriorAuthorization[],
  taskPriorAuthorization?: PriorAuthorization | null
): ReferralAuthOnFileSnapshot {
  const selectedPa = selectReferralAuthTrackingPriorAuthorization(linkedPriorAuths, taskPriorAuthorization);

  const authorizationNumber = referral.authorizationNumber?.trim() || selectedPa?.authorizationNumber?.trim() || "";
  const startDate = formatAuthDate(selectedPa?.startDate);
  const endDate = formatAuthDate(selectedPa?.endDate);
  const cptCodes =
    formatCptCodes(referral.commonProcedureTerminologyList) ||
    formatCptCodes(selectedPa?.commonProcedureTerminologyList);
  const visitsValue = referral.numberOfVisits ?? selectedPa?.numberOfServices;
  const visits = visitsValue != null && Number.isFinite(visitsValue) && visitsValue > 0 ? String(visitsValue) : "";

  return {
    authorizationNumber,
    startDate,
    endDate,
    cptCodes,
    visits,
    hasAuthOnFile: isReferralAuthCompleteFields({
      authorizationNumber,
      requestId: selectedPa?.requestId,
      startDate,
      endDate,
      numberOfServices: visitsValue,
    }),
  };
}
